const logger = console.scope('RTC')

// Ring buffer — 5 minutes at 1 sample/sec
const RING_BUFFER_SIZE = 300
const SUMMARY_INTERVAL = 10
const HEALTH_WINDOW = 30

const statsBuffer = []
let sampleCount = 0

// Performance thresholds by network scenario
//
// Causal chain: out_fps (client encode/send) → server processes → in_fps (server sends back) in_fps can never exceed out_fps. out_fps drops on sudden movement (encoder overload) or bandwidth constraints. in_fps is further reduced by server processing time. Server FPS is always somewhat below target due to processing latency.
const THRESHOLDS = {
	stable_high: {
		label: 'Stable high-bandwidth',
		detect: (rtt, lossRate) => rtt < 0.03 && lossRate < 0.001,
		min_out_fps: 28,
		min_in_fps: 24,
		min_out_fps_steady: 29,
		min_in_bps: 7_000_000,
		max_rtt_s: 0.03,
		max_loss_pct: 0.1,
		max_pli_per_30s: 1,
		min_ql_none_pct: 95,
		max_frames_dropped: 0,
		max_out_fps_dip_duration: 3,
		out_fps_dip_threshold: 15,
	},
	stable_high_loss: {
		label: 'Stable high-bandwidth with drops',
		detect: (rtt, lossRate) => rtt < 0.03 && lossRate >= 0.001 && lossRate < 0.03,
		min_out_fps: 25,
		min_in_fps: 20,
		min_in_bps: 6_000_000,
		max_rtt_s: 0.03,
		max_loss_pct: 3,
		max_pli_per_30s: 3,
		recovery_out_fps_target: 28,
		recovery_out_fps_time_s: 5,
		recovery_in_fps_target: 22,
		recovery_in_fps_time_s: 8,
	},
	stable_average: {
		label: 'Stable average bandwidth',
		detect: (rtt, lossRate) => rtt >= 0.03 && rtt < 0.08 && lossRate < 0.01,
		min_out_fps: 25,
		min_in_fps: 20,
		min_in_bps: 4_000_000,
		max_rtt_s: 0.08,
		max_loss_pct: 1,
		max_ql_bw_pct: 20,
	},
	dynamic: {
		label: 'Dynamic network',
		detect: () => true,
		min_out_fps: 20,
		min_in_fps: 15,
		recovery_out_fps_target: 25,
		recovery_out_fps_time_s: 10,
		max_freeze_consecutive: 2,
	},
}

function median(arr) {
	if (!arr.length) return 0
	const sorted = [...arr].sort((a, b) => a - b)
	const mid = Math.floor(sorted.length / 2)
	return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

function avg(arr) {
	if (!arr.length) return 0
	return arr.reduce((s, v) => s + v, 0) / arr.length
}

function deltaCounter(samples, key) {
	const first = samples[0]?.[key]
	const last = samples[samples.length - 1]?.[key]
	if (first == null || last == null) return 0
	return Math.max(0, last - first)
}

function formatBps(bps) {
	if (bps == null) return '?'
	return (bps / 1_000_000).toFixed(1) + 'M'
}

function emitSummary(window10) {
	const last = window10[window10.length - 1]
	if (!last) return

	const inFps = Math.round(avg(window10.map(e => e.in_fps).filter(v => v != null)))
	const outFps = Math.round(avg(window10.map(e => e.out_fps).filter(v => v != null)))
	const inBps = formatBps(avg(window10.map(e => e.in_bps).filter(v => v != null)))
	const outBps = formatBps(avg(window10.map(e => e.out_bps).filter(v => v != null)))
	const rttMs = Math.round((avg(window10.map(e => e.rtt).filter(v => v != null)) || 0) * 1000)

	const dNackIn = deltaCounter(window10, 'nack_in')
	const dNackOut = deltaCounter(window10, 'nack_out')
	const dPliIn = deltaCounter(window10, 'pli_in')
	const dPliOut = deltaCounter(window10, 'pli_out')
	const dKfEnc = deltaCounter(window10, 'kf_enc')
	const dKfDec = deltaCounter(window10, 'kf_dec')

	const lossRate = last.fraction_lost != null ? (last.fraction_lost * 100).toFixed(1) + '%' : '?'
	const ql = last.ql_reason ?? '?'
	const avail = formatBps(last.avail_out_bps)

	logger.warn(
		`out=${outFps}fps/${outBps} in=${inFps}fps/${inBps} rtt=${rttMs}ms loss=${lossRate} ` +
			`nack=${dNackIn}/${dNackOut} pli=${dPliIn}/${dPliOut} kf=${dKfEnc}/${dKfDec} ql=${ql} avail=${avail}`
	)
}

export function getLatestStats() {
	const last = statsBuffer[statsBuffer.length - 1]
	if (!last) return null
	return {
		out_fps: last.out_fps,
		in_fps: last.in_fps,
		rtt: last.rtt,
		out_bps: last.out_bps,
		in_bps: last.in_bps,
		fraction_lost: last.fraction_lost,
	}
}

export function pushStats(entry) {
	statsBuffer.push(entry)
	while (statsBuffer.length > RING_BUFFER_SIZE) {
		statsBuffer.shift()
	}

	sampleCount++
	if (sampleCount % SUMMARY_INTERVAL === 0) {
		const window10 = statsBuffer.slice(-SUMMARY_INTERVAL)
		emitSummary(window10)
	}
}

function detectScenario(samples) {
	const rtts = samples.map(e => e.rtt).filter(v => v != null)
	const medRtt = median(rtts)

	// Compute loss rate from fraction_lost averages
	const losses = samples.map(e => e.fraction_lost).filter(v => v != null)
	const avgLoss = avg(losses)

	for (const [key, th] of Object.entries(THRESHOLDS)) {
		if (th.detect(medRtt, avgLoss)) {
			return key
		}
	}
	return 'dynamic'
}

function assessHealth() {
	const samples = statsBuffer.slice(-HEALTH_WINDOW)
	if (samples.length < 5) {
		return { status: 'unknown', issues: ['Not enough data (need ≥5 samples)'], averages: {}, scenario: 'unknown' }
	}

	const scenario = detectScenario(samples)
	const th = THRESHOLDS[scenario]
	const issues = []

	// Compute averages
	const vals = key => samples.map(e => e[key]).filter(v => v != null)

	// Dominant quality limitation reason
	const qlCounts = {}
	for (const s of samples) {
		if (s.ql_reason) qlCounts[s.ql_reason] = (qlCounts[s.ql_reason] || 0) + 1
	}
	const qlDominant = Object.entries(qlCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'none'

	const averages = {
		out_fps: Math.round(avg(vals('out_fps'))),
		in_fps: Math.round(avg(vals('in_fps'))),
		fps_gap: Math.round(avg(vals('out_fps'))) - Math.round(avg(vals('in_fps'))),
		out_bps: Math.round(avg(vals('out_bps'))),
		in_bps: Math.round(avg(vals('in_bps'))),
		rtt_ms: Math.round((avg(vals('rtt')) || 0) * 1000),
		loss_pct: parseFloat(((avg(vals('fraction_lost')) || 0) * 100).toFixed(2)),
		pli_delta: deltaCounter(samples, 'pli_out'),
		nack_delta: deltaCounter(samples, 'nack_out'),
		kf_enc_delta: deltaCounter(samples, 'kf_enc'),
		kf_dec_delta: deltaCounter(samples, 'kf_dec'),
		frames_dropped: deltaCounter(samples, 'frames_dropped'),
		avail_out_bps: Math.round(avg(vals('avail_out_bps'))),
		target_bps: Math.round(avg(vals('target_bps'))),
		ql_reason_dominant: qlDominant,
	}

	// --- Outbound FPS (client encode → server) ---
	if (th.min_out_fps != null && averages.out_fps < th.min_out_fps) {
		const reason =
			averages.ql_reason_dominant === 'cpu'
				? ' (CPU-limited encoder)'
				: averages.ql_reason_dominant === 'bandwidth'
					? ' (bandwidth-limited)'
					: ''
		issues.push(`Outbound FPS ${averages.out_fps} < ${th.min_out_fps} target${reason}`)
	}

	// Detect out_fps dips (sudden movement causing encoder stalls)
	if (th.max_out_fps_dip_duration != null) {
		let maxDip = 0
		let current = 0
		for (const s of samples) {
			if (s.out_fps != null && s.out_fps < (th.out_fps_dip_threshold ?? 15)) {
				current++
				maxDip = Math.max(maxDip, current)
			} else {
				current = 0
			}
		}
		if (maxDip > th.max_out_fps_dip_duration) {
			issues.push(
				`Outbound FPS dip: ${maxDip}s below ${th.out_fps_dip_threshold ?? 15} fps (max ${th.max_out_fps_dip_duration}s)`
			)
		}
	}

	// Steady-state out_fps check (excludes dips — uses 75th percentile)
	if (th.min_out_fps_steady != null) {
		const outVals = vals('out_fps')
		if (outVals.length >= 10) {
			const sorted = [...outVals].sort((a, b) => a - b)
			const p75 = sorted[Math.floor(sorted.length * 0.75)]
			if (p75 < th.min_out_fps_steady) {
				issues.push(`Outbound FPS p75=${p75} < ${th.min_out_fps_steady} steady target`)
			}
		}
	}

	// --- Inbound FPS (server → client, always ≤ out_fps) ---
	if (th.min_in_fps != null && averages.in_fps < th.min_in_fps) {
		const gap = averages.out_fps - averages.in_fps
		const detail = gap > 5 ? ` (${gap} fps below outbound — server processing bottleneck)` : ''
		issues.push(`Inbound FPS ${averages.in_fps} < ${th.min_in_fps} target${detail}`)
	}

	// --- Bitrate ---
	if (th.min_in_bps != null && averages.in_bps < th.min_in_bps) {
		issues.push(
			`Inbound bitrate ${(averages.in_bps / 1e6).toFixed(1)} Mbps < ${(th.min_in_bps / 1e6).toFixed(1)} Mbps target`
		)
	}

	// --- Network ---
	if (th.max_rtt_s != null && averages.rtt_ms > th.max_rtt_s * 1000) {
		issues.push(`RTT ${averages.rtt_ms}ms > ${th.max_rtt_s * 1000}ms target`)
	}
	if (th.max_loss_pct != null && averages.loss_pct > th.max_loss_pct) {
		issues.push(`Packet loss ${averages.loss_pct}% > ${th.max_loss_pct}% target`)
	}
	if (th.max_pli_per_30s != null && averages.pli_delta > th.max_pli_per_30s) {
		issues.push(`PLI requests ${averages.pli_delta} > ${th.max_pli_per_30s} per ${HEALTH_WINDOW}s`)
	}

	// --- Quality limitation ---
	if (th.min_ql_none_pct != null) {
		const qlNone = samples.filter(e => e.ql_reason === 'none').length
		const qlNonePct = (qlNone / samples.length) * 100
		if (qlNonePct < th.min_ql_none_pct) {
			issues.push(`Quality unlimited only ${qlNonePct.toFixed(0)}% of time (target ≥${th.min_ql_none_pct}%)`)
		}
	}
	if (th.max_ql_bw_pct != null) {
		const qlBw = samples.filter(e => e.ql_reason === 'bandwidth').length
		const qlBwPct = (qlBw / samples.length) * 100
		if (qlBwPct > th.max_ql_bw_pct) {
			issues.push(`Bandwidth-limited ${qlBwPct.toFixed(0)}% of time (target ≤${th.max_ql_bw_pct}%)`)
		}
	}

	// --- Freeze detection (inbound) ---
	if (th.max_freeze_consecutive != null) {
		let maxConsecutiveZero = 0
		let current = 0
		for (const s of samples) {
			if (s.in_fps === 0 || s.in_fps == null) {
				current++
				maxConsecutiveZero = Math.max(maxConsecutiveZero, current)
			} else {
				current = 0
			}
		}
		if (maxConsecutiveZero > th.max_freeze_consecutive) {
			issues.push(
				`Video freeze: ${maxConsecutiveZero}s consecutive 0 inbound fps (max ${th.max_freeze_consecutive}s)`
			)
		}
	}

	// --- Frames dropped ---
	if (th.max_frames_dropped != null && averages.frames_dropped > th.max_frames_dropped) {
		issues.push(`${averages.frames_dropped} frames dropped in ${HEALTH_WINDOW}s`)
	}

	// --- Recovery checks (separate for out and in) ---
	const checkRecovery = (key, target, timeLimit, label) => {
		if (target == null || timeLimit == null) return
		for (let i = 1; i < samples.length; i++) {
			const prev = samples[i - 1]?.[key]
			const curr = samples[i]?.[key]
			if (prev != null && prev >= 10 && (curr === 0 || curr == null)) {
				let recoveredAt = null
				for (let j = i + 1; j < samples.length; j++) {
					if (samples[j]?.[key] != null && samples[j][key] >= target) {
						recoveredAt = j - i
						break
					}
				}
				if (recoveredAt != null && recoveredAt > timeLimit) {
					issues.push(`${label} recovery took ${recoveredAt}s after drop (target ≤${timeLimit}s)`)
				} else if (recoveredAt == null) {
					issues.push(`${label} did not recover to ${target} fps after drop`)
				}
				break
			}
		}
	}
	checkRecovery('out_fps', th.recovery_out_fps_target, th.recovery_out_fps_time_s, 'Outbound FPS')
	checkRecovery('in_fps', th.recovery_in_fps_target, th.recovery_in_fps_time_s, 'Inbound FPS')

	// --- Status determination ---
	let status
	const hasSustainedFreeze = samples.filter(e => e.in_fps === 0 || e.in_fps == null).length > 5
	const hasOutFpsCollapse = samples.filter(e => e.out_fps === 0 || e.out_fps == null).length > 3
	if (hasSustainedFreeze || hasOutFpsCollapse || issues.length >= 3) {
		status = 'bad'
	} else if (issues.length > 0) {
		status = 'degraded'
	} else {
		status = 'good'
	}

	return { status, issues, averages, scenario: th.label }
}

function exportStats() {
	return JSON.parse(JSON.stringify(statsBuffer))
}

// Expose on window for Playwright / devtools
if (typeof window !== 'undefined') {
	window.__rtc_stats_log = statsBuffer
	window.__rtc_export_stats = exportStats
	window.__rtc_health = assessHealth
}
